import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Suspense, lazy, useEffect } from 'react';
import { PublicOnlyLayout } from '@/components/layout/public-only-layout';
import { DEFAULT_LANGUAGE } from '@/i18n';
import { useLocale } from '@/hooks/use-locale';
import { Skeleton } from '@/components/ui/skeleton';

const LandingPage = lazy(() => import('@/pages/landing-page').then((m) => ({ default: m.LandingPage })));
const ServicesPage = lazy(() => import('@/pages/services-page').then((m) => ({ default: m.ServicesPage })));
const PortfolioPage = lazy(() => import('@/pages/portfolio-page').then((m) => ({ default: m.PortfolioPage })));
const PortfolioDetailPage = lazy(() => import('@/pages/portfolio-detail-page').then((m) => ({ default: m.PortfolioDetailPage })));
const ProcessPage = lazy(() => import('@/pages/process-page').then((m) => ({ default: m.ProcessPage })));
const AboutPage = lazy(() => import('@/pages/about-page').then((m) => ({ default: m.AboutPage })));
const FaqPage = lazy(() => import('@/pages/faq-page').then((m) => ({ default: m.FaqPage })));
const ContactPage = lazy(() => import('@/pages/contact-page').then((m) => ({ default: m.ContactPage })));
const GtfsSeoPage = lazy(() => import('@/pages/gtfs-seo-page').then((m) => ({ default: m.GtfsSeoPage })));
const LegalPage = lazy(() => import('@/pages/legal-page').then((m) => ({ default: m.LegalPage })));
const NotFoundPage = lazy(() => import('@/pages/not-found-page').then((m) => ({ default: m.NotFoundPage })));

function PageSkeleton() {
  return (
    <div className="container mx-auto py-8 space-y-4">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

function RootRedirect() {
  return <Navigate to={`/${DEFAULT_LANGUAGE}/`} replace />;
}

function NotFoundInLang() {
  const { lang } = useLocale();
  return <NotFoundPage lang={lang} />;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function PublicRoutes() {
  return (
    <>
      <ScrollToTop />
      <Suspense fallback={<PageSkeleton />}>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/:lang" element={<PublicOnlyLayout />}>
            <Route index element={<LandingPage />} />
            <Route path="servicios" element={<ServicesPage />} />
            <Route path="portfolio" element={<PortfolioPage />} />
            <Route path="portfolio/:slug" element={<PortfolioDetailPage />} />
            <Route path="proceso" element={<ProcessPage />} />
            <Route path="sobre-el-servicio" element={<AboutPage />} />
            <Route path="faq" element={<FaqPage />} />
            <Route path="contacto" element={<ContactPage />} />
            <Route path="crear-feed-gtfs" element={<GtfsSeoPage pageKey="creation" />} />
            <Route path="mantenimiento-gtfs" element={<GtfsSeoPage pageKey="maintenance" />} />
            <Route path="validar-feed-gtfs" element={<GtfsSeoPage pageKey="validation" />} />
            <Route path="gtfs-feed-creation" element={<GtfsSeoPage pageKey="creation" />} />
            <Route path="gtfs-feed-maintenance" element={<GtfsSeoPage pageKey="maintenance" />} />
            <Route path="gtfs-feed-validation" element={<GtfsSeoPage pageKey="validation" />} />
            <Route path="legal/privacidad" element={<LegalPage document="privacy" />} />
            <Route path="legal/terminos" element={<LegalPage document="terms" />} />
            <Route path="legal/cookies" element={<LegalPage document="cookies" />} />
            <Route path="*" element={<NotFoundInLang />} />
          </Route>
          <Route path="*" element={<RootRedirect />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default function PublicApp() {
  return (
    <BrowserRouter>
      <PublicRoutes />
    </BrowserRouter>
  );
}
